import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Shield, Lock, Eye, Activity, AlertTriangle, CheckCircle,
  Smartphone, Mail, Clock, FileText, Settings, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecurityCenterPage() {
  const { user, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('status');
  const [securityStatus, setSecurityStatus] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, alertsRes, activityRes, settingsRes] = await Promise.all([
        axios.get(`${API}/security/status`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/security/alerts`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/security/activity-log`, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/security/settings`, { ...getAuthHeader(), withCredentials: true })
      ]);
      setSecurityStatus(statusRes.data);
      setAlerts(alertsRes.data);
      setActivityLog(activityRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      await axios.put(`${API}/security/settings`, updates, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setSettings(prev => ({ ...prev, ...updates }));
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API}/security/alerts/${alertId}/acknowledge`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, acknowledged: true } : a));
      toast.success('Alert acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await axios.put(`${API}/security/alerts/${alertId}/resolve`, 
        { notes: 'Resolved by user' },
        { ...getAuthHeader(), withCredentials: true }
      );
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, resolved: true } : a));
      toast.success('Alert resolved');
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-500 bg-red-500/20';
      case 'high': return 'text-red-400 bg-red-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'low': return 'text-green-400 bg-green-500/20';
      default: return 'text-slate-300 bg-slate-500/20';
    }
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Security Center"
        description="Monitor and protect your account and data"
      />

      {/* Security Score Banner */}
      {securityStatus && (
        <GlassCard className="mb-8 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-500/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold bg-slate-800 ${getScoreColor(securityStatus.security_score)}`}>
                {securityStatus.security_score}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Security Score</h2>
                <p className="text-slate-300">Your account protection level</p>
              </div>
            </div>
            <div className="flex gap-6 text-center">
              <div>
                <p className="text-2xl font-bold text-white">{securityStatus.recent_alerts_count}</p>
                <p className="text-sm text-slate-300">Recent Alerts</p>
              </div>
              <div>
                <p className={`text-2xl font-bold ${securityStatus.unresolved_alerts_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {securityStatus.unresolved_alerts_count}
                </p>
                <p className="text-sm text-slate-300">Unresolved</p>
              </div>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'status', label: 'Status', icon: Shield },
          { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
          { id: 'activity', label: 'Activity Log', icon: Activity },
          { id: 'settings', label: 'Settings', icon: Settings },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-navy-800 text-slate-300 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Status Tab */}
      {activeTab === 'status' && securityStatus && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <GlassCard className="text-center">
              <Lock className={`w-8 h-8 mx-auto mb-2 ${securityStatus.two_factor_enabled ? 'text-green-400' : 'text-red-400'}`} />
              <p className="text-white font-semibold">Two-Factor Auth</p>
              <p className={`text-sm ${securityStatus.two_factor_enabled ? 'text-green-400' : 'text-red-400'}`}>
                {securityStatus.two_factor_enabled ? 'Enabled' : 'Disabled'}
              </p>
            </GlassCard>
            <GlassCard className="text-center">
              <Bell className={`w-8 h-8 mx-auto mb-2 ${securityStatus.login_alerts_enabled ? 'text-green-400' : 'text-slate-300'}`} />
              <p className="text-white font-semibold">Login Alerts</p>
              <p className={`text-sm ${securityStatus.login_alerts_enabled ? 'text-green-400' : 'text-slate-300'}`}>
                {securityStatus.login_alerts_enabled ? 'Active' : 'Inactive'}
              </p>
            </GlassCard>
            <GlassCard className="text-center">
              <Activity className={`w-8 h-8 mx-auto mb-2 ${securityStatus.activity_monitoring_enabled ? 'text-green-400' : 'text-slate-300'}`} />
              <p className="text-white font-semibold">Activity Monitoring</p>
              <p className={`text-sm ${securityStatus.activity_monitoring_enabled ? 'text-green-400' : 'text-slate-300'}`}>
                {securityStatus.activity_monitoring_enabled ? 'Active' : 'Inactive'}
              </p>
            </GlassCard>
            <GlassCard className="text-center">
              <FileText className={`w-8 h-8 mx-auto mb-2 ${securityStatus.document_encryption_enabled ? 'text-green-400' : 'text-slate-300'}`} />
              <p className="text-white font-semibold">Document Encryption</p>
              <p className={`text-sm ${securityStatus.document_encryption_enabled ? 'text-green-400' : 'text-slate-300'}`}>
                {securityStatus.document_encryption_enabled ? 'Enabled' : 'Disabled'}
              </p>
            </GlassCard>
          </div>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Score Breakdown</h3>
            <div className="space-y-3">
              {securityStatus.score_breakdown && Object.entries(securityStatus.score_breakdown).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-slate-300 capitalize">{key.replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${value > 0 ? 'bg-green-500' : 'bg-slate-600'}`}
                        style={{ width: `${(value / 30) * 100}%` }}
                      />
                    </div>
                    <span className={`font-semibold ${value > 0 ? 'text-green-400' : 'text-slate-300'}`}>
                      {value}/30
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Recommendations</h3>
            <ul className="space-y-3">
              {securityStatus.security_recommendations?.map((rec, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">{rec}</span>
                </li>
              ))}
            </ul>
          </GlassCard>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {alerts.length === 0 ? (
            <GlassCard>
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                <p className="text-white font-semibold">No Security Alerts</p>
                <p className="text-slate-300">Your account is secure</p>
              </div>
            </GlassCard>
          ) : (
            alerts.map((alert, i) => (
              <GlassCard 
                key={alert.id || i}
                data-testid={`alert-${i}`}
                className={`${!alert.resolved ? 'border-red-500/30' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getSeverityColor(alert.severity)}`}>
                      <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-white">{alert.title}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm mt-1">{alert.description}</p>
                      <div className="flex gap-4 mt-2 text-xs text-slate-300">
                        <span>Type: {alert.alert_type}</span>
                        {alert.ip_address && <span>IP: {alert.ip_address}</span>}
                        <span>{new Date(alert.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  {!alert.resolved && (
                    <div className="flex gap-2">
                      {!alert.acknowledged && (
                        <Button variant="outline" size="sm" onClick={() => acknowledgeAlert(alert.id)}>
                          Acknowledge
                        </Button>
                      )}
                      <Button size="sm" onClick={() => resolveAlert(alert.id)}>
                        Resolve
                      </Button>
                    </div>
                  )}
                  {alert.resolved && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm">
                      Resolved
                    </span>
                  )}
                </div>
              </GlassCard>
            ))
          )}
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <GlassCard>
          <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
          {activityLog.length === 0 ? (
            <p className="text-slate-300 text-center py-8">No recent activity</p>
          ) : (
            <div className="space-y-3">
              {activityLog.map((activity, i) => (
                <div 
                  key={activity.id || i}
                  data-testid={`activity-${i}`}
                  className="flex items-center justify-between p-3 bg-navy-800/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Activity className={`w-5 h-5 ${activity.flagged ? 'text-red-400' : 'text-slate-300'}`} />
                    <div>
                      <p className="text-white">{activity.description}</p>
                      <p className="text-slate-300 text-xs">{activity.activity_type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-300 text-sm">{new Date(activity.created_at).toLocaleString()}</p>
                    {activity.ip_address && (
                      <p className="text-slate-300 text-xs">{activity.ip_address}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && settings && (
        <div className="space-y-6">
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Authentication</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Two-Factor Authentication</p>
                  <p className="text-slate-300 text-sm">Add an extra layer of security</p>
                </div>
                <button
                  onClick={() => updateSettings({ two_factor_enabled: !settings.two_factor_enabled })}
                  data-testid="toggle-2fa"
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.two_factor_enabled ? 'bg-green-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    settings.two_factor_enabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Alert Preferences</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Login Alert Emails</p>
                  <p className="text-slate-300 text-sm">Get notified of new sign-ins</p>
                </div>
                <button
                  onClick={() => updateSettings({ login_alert_email: !settings.login_alert_email })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.login_alert_email ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    settings.login_alert_email ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Suspicious Activity Alerts</p>
                  <p className="text-slate-300 text-sm">Alert on unusual account activity</p>
                </div>
                <button
                  onClick={() => updateSettings({ suspicious_activity_alert: !settings.suspicious_activity_alert })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.suspicious_activity_alert ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    settings.suspicious_activity_alert ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4">Session & Privacy</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Session Timeout</p>
                  <p className="text-slate-300 text-sm">Auto logout after inactivity</p>
                </div>
                <select 
                  value={settings.session_timeout_minutes || 60}
                  onChange={(e) => updateSettings({ session_timeout_minutes: parseInt(e.target.value) })}
                  className="bg-navy-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={240}>4 hours</option>
                </select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Require Password for Sensitive Docs</p>
                  <p className="text-slate-300 text-sm">Extra protection for sensitive files</p>
                </div>
                <button
                  onClick={() => updateSettings({ require_password_for_sensitive: !settings.require_password_for_sensitive })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    settings.require_password_for_sensitive ? 'bg-blue-600' : 'bg-slate-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    settings.require_password_for_sensitive ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          </GlassCard>
        </div>
      )}
    </AppLayout>
  );
}
